import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const defaultProfileSettings = {
  type: "PROFILE_CATALOG_SETTINGS",
  profileTable: {},
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_108: DocMigration = {
  maxSchemaVersion: "0.0.108",
  description: "Add default profiles directly to first profile table.",
  apply(json: any) {
    // 1. Create a first settings set if it doesn't exist. Reference first settings set id.
    let firstSettingsSetId: string | undefined = undefined;
    for (const settingsSetId in json.settingsSets) {
      const settingsSet = json.settingsSets[settingsSetId];
      if (settingsSet.sortIndex !== 0) {
        continue;
      }
      firstSettingsSetId = settingsSet.id;
    }

    if (firstSettingsSetId === undefined) {
      const baseSettingsSetId = uid();
      (json.settingsSets as any) = {};
      (json.settingsSets[baseSettingsSetId] as any) = {
        entityType: "SETTINGS_SET",
        name: "Base Settings Set",
        id: baseSettingsSetId,
        sortIndex: 0,
      };
      firstSettingsSetId = baseSettingsSetId;
    }

    let firstSettingsSetProfileSettingsId: string | undefined = undefined;

    for (const settingsId in json.settings) {
      const settings = json.settings[settingsId] as any;
      if ((settings.type as any) === "PROFILE_CATALOG_SETTINGS") {
        if (firstSettingsSetId !== undefined && settings.parentId !== undefined) {
          if (settings.parentId === firstSettingsSetId) {
            firstSettingsSetProfileSettingsId = settingsId;
            (json.settings[settingsId] as any) = {
              ...settings,
              profileTable: { ...settings.profileTable, ...legacyDefaultProfileSettings },
            };
          }
        }
      }
    }

    const newSettingsId = uid();

    if (!firstSettingsSetProfileSettingsId) {
      (json.settings[newSettingsId] as any) = {
        ...{ ...defaultProfileSettings, profileTable: legacyDefaultProfileSettings },
        entityType: "SETTINGS",
        id: newSettingsId,
        parentId: firstSettingsSetId,
        sortIndex: 0,
      };
    }

    return json;
  },
};

export default migration_0_0_108;

const legacyDefaultProfileSettings: { [key: string]: any } = {
  ["EXTERIOR_TRANSITION_BOARD_TRIM_PROFILE"]: {
    name: "Exterior Transition Board Trim",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Exterior Transition Board Trim",
    width: 0.75,
    height: 7.25,
    nominalWidth: 1,
    nominalHeight: 8,
    elevationNote: '8"',
  },
  ["DRIP_CAP_PROFILE"]: {
    name: "Drip Cap",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Drip Cap",
    polyline: [0, 0, 0, 0.5, 1, 0.5, 1, 0, 0, 0, 0, 0],
    elevationNote: "DRIP CAP",
  },
  ["ROWLOCK_BRICK"]: {
    name: "Rowlock Brick",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Rowlock Brick",
    polyline: [0, 0, 1, 0, 1, -3.5, 0, -3.5, 0, 0],
    elevationNote: "ROWLOCK",
  },
  ["ROWLOCK_OVER_SOLDIER_BRICK"]: {
    name: "Rowlock Soldier Brick",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Rowlock Soldier Brick",
    polyline: [0, 0, 2, 0, 2, -3.5, 1, -3.5, 1, -11, 0, -11, 0, 0],
    elevationNote: "ROWLOCK SOLDIER",
  },
  ["TRIM_BOARD_1_2"]: {
    name: "Trim Board 1 x 2",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 2",
    width: 0.75,
    height: 1.5,
    nominalWidth: 1,
    nominalHeight: 2,
    elevationNote: '2"',
  },
  ["TRIM_BOARD_1_3"]: {
    name: "Trim Board 1 x 3",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 3",
    width: 0.75,
    height: 2.5,
    nominalWidth: 1,
    nominalHeight: 3,
    elevationNote: '3"',
  },
  ["TRIM_BOARD_1_4"]: {
    name: "Trim Board 1 x 4",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 4",
    width: 0.75,
    height: 3.5,
    nominalWidth: 1,
    nominalHeight: 4,
    elevationNote: '4"',
  },
  ["TRIM_BOARD_1_6"]: {
    name: "Trim Board 1 x 6",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 6",
    width: 0.75,
    height: 5.5,
    nominalWidth: 1,
    nominalHeight: 6,
    elevationNote: '6"',
  },
  ["TRIM_BOARD_1_8"]: {
    name: "Trim Board 1 x 8",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 8",
    width: 0.75,
    height: 7.25,
    nominalWidth: 1,
    nominalHeight: 8,
    elevationNote: '8"',
  },
  ["TRIM_BOARD_1_10"]: {
    name: "Trim Board 1 x 10",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 10",
    width: 0.75,
    height: 9.25,
    nominalWidth: 1,
    nominalHeight: 10,
    elevationNote: '10"',
  },
  ["TRIM_BOARD_1_12"]: {
    name: "Trim Board 1 x 12",
    entityType: "PROFILE_EXTRUSION",
    profileType: "BOARD",
    elevationNoteEnabled: false,
    bomDescription: "Trim Board 1 x 12",
    width: 0.75,
    height: 11.25,
    nominalWidth: 1,
    nominalHeight: 12,
    elevationNote: '12"',
  },
  ["TRIM_REVEAL_1"]: {
    name: "Reveal 1",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Reveal 1",
    polyline: [0, 0, 0, 0.5, 1, 0.5, 1, -0.5, 0, -0.5, 0, 0],
    elevationNote: 'REVEAL 1"',
  },
  ["TRIM_CAP_1_2"]: {
    name: "Cap 2",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Cap 2",
    polyline: [0, 0, 0, 3, 1.5, 3, 1.5, 0, 0, 0, 0, 0],
    elevationNote: 'CAP 2"',
  },
  ["TRIM_VERT_1_60"]: {
    name: "Trim Vert 1 x 60",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Trim Vert 1 x 60",
    polyline: [0, 0, 0, 60, 0.75, 60, 0.75, 0, 0, 0, 0, 0],
    elevationNote: '60"',
  },
  ["CROWN_4_FRIEZE_6"]: {
    name: "Crown 4 Frieze 6",
    entityType: "PROFILE_EXTRUSION",
    profileType: "CUSTOM",
    elevationNoteEnabled: false,
    bomDescription: "Crown 4 Frieze 6",
    polyline: [
      0, 0, -3.357881, 0, -3.357881, -0.76815, -2.800829, -1.194753, -2.539932, -1.903407, -1.968778, -2.312381,
      -1.968778, -2.664945, -1.556278, -2.83065, -1.439932, -3.285458, -0.75, -3.285458, -0.75, -7.5, 0, -7.5, 0, 0,
    ],
    reflectOverY: true,
    elevationNote: "CROWN",
  },
};
